import { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './SearchableSelect.css';

/**
 * Sunucu taraflı arama yapan tek-seçim combobox.
 * Büyük lookup listeleri (8000+ ürün, 4000+ müşteri) için tasarlandı —
 * tüm listeyi belleğe yüklemez, kullanıcı yazdıkça API'den arar.
 *
 * Props:
 *   value                 : seçili id (string)
 *   selectedLabel         : seçili id'nin etiketi (biliniyorsa; düzenleme modunda ön-dolum)
 *   onChange(value, item) : seçim değişince çağrılır (item = {value, label} | null)
 *   fetchOptions(query)   : Promise<[{value, label}]> — sunucudan arama
 *   disabled              : salt-okunur
 *   placeholder           : arama kutusu placeholder'ı
 *   emptyOption           : "(Tümü)" gibi boş seçim etiketi (verilirse temizleme seçeneği çıkar)
 *   minChars              : aramayı tetikleyen min karakter (default 1)
 *   pageSize              : verilirse SAYFALI + sonsuz kaydırma modu — boş sorguyla açılınca ilk
 *                           pageSize kayıt gelir, aşağı kaydırdıkça sonrakiler yüklenir.
 *                           Bu modda fetchOptions(query, skip) çağrılır (skip ile sayfalama).
 */
export function SearchableSelect({
  value,
  selectedLabel,
  onChange,
  fetchOptions,
  disabled = false,
  placeholder = 'Ara…',
  emptyOption,
  minChars = 1,
  pageSize,
}) {
  const paged = typeof pageSize === 'number' && pageSize > 0;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const [labelCache, setLabelCache] = useState({});
  const [pos, setPos] = useState({ top: 0, left: 0, width: 240, maxHeight: 320 });

  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const reqIdRef = useRef(0);

  // Dışarıdan gelen seçili etiketi cache'le (düzenleme modunda görünsün)
  useEffect(() => {
    if (value && selectedLabel) {
      setLabelCache((c) => (c[value] ? c : { ...c, [value]: selectedLabel }));
    }
  }, [value, selectedLabel]);

  // value dolu ama etiketi yok (ör. kaydı "view" ile açınca) → sunucudan çöz.
  // Küçük lookup'larda (distribütör vb.) ilk sayfadan eşleşeni bulup cache'ler.
  useEffect(() => {
    if (!value || selectedLabel || labelCache[value] || typeof fetchOptions !== 'function') return undefined;
    let alive = true;
    (async () => {
      try {
        const opts = (await fetchOptions('', 0)) || [];
        const found = opts.find((o) => String(o.value) === String(value));
        if (alive && found) setLabelCache((c) => (c[value] ? c : { ...c, [value]: found.label }));
      } catch { /* yoksay */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, selectedLabel, fetchOptions]);

  const displayLabel = value ? (labelCache[value] || selectedLabel || 'Seçili kayıt') : '';

  // Debounce'lu sunucu araması / ilk sayfa. Paged modda boş sorgu da ilk sayfayı yükler.
  useEffect(() => {
    if (!open) return undefined;
    const q = query.trim();
    if (!paged && q.length < minChars) { setResults([]); setLoading(false); setHasMore(false); return undefined; }
    setLoading(true);
    const reqId = reqIdRef.current + 1;
    reqIdRef.current = reqId;
    const t = setTimeout(async () => {
      try {
        const opts = (await fetchOptions(q, 0)) || [];
        if (reqIdRef.current === reqId) {
          setResults(opts);
          setSkip(opts.length);
          setHasMore(paged ? opts.length >= pageSize : false);
          setHighlight(opts.length ? 0 : -1);
        }
      } catch {
        if (reqIdRef.current === reqId) { setResults([]); setHasMore(false); }
      } finally {
        if (reqIdRef.current === reqId) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, open, minChars, fetchOptions, paged, pageSize]);

  // Sonsuz kaydırma — sonraki sayfayı yükle ve sona ekle.
  const loadMore = async () => {
    if (!paged || loadingMore || loading || !hasMore) return;
    const reqId = reqIdRef.current;
    const currentSkip = skip;
    setLoadingMore(true);
    try {
      const opts = (await fetchOptions(query.trim(), currentSkip)) || [];
      if (reqIdRef.current !== reqId) return; // arada yeni arama başladı → at
      setResults((prev) => [...prev, ...opts]);
      setSkip(currentSkip + opts.length);
      setHasMore(opts.length >= pageSize);
    } catch {
      // yoksay
    } finally {
      setLoadingMore(false);
    }
  };

  const onListScroll = (e) => {
    if (!paged) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) loadMore();
  };

  // Portal konumlandırma — viewport'a göre aşağı/yukarı aç, ekrana sığdır.
  const computePos = useCallback(() => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    const margin = 8;
    const desired = 320; // arama kutusu + liste için yaklaşık ideal yükseklik
    const spaceBelow = vh - r.bottom - margin;
    const spaceAbove = r.top - margin;
    // Aşağıda yeterli yer yoksa ve yukarıda daha çok yer varsa yukarı aç.
    // Yukarı açılırken üstten DEĞİL alttan sabitle (input'a yapışsın); aksi halde içerik
    // kısa olduğunda (ör. 2 dağıtıcı) popup max yüksekliğe göre çok yukarıda asılı kalır.
    if (spaceBelow < Math.min(desired, 220) && spaceAbove > spaceBelow) {
      const maxHeight = Math.max(140, Math.min(desired, spaceAbove));
      setPos({ top: undefined, bottom: Math.max(margin, vh - r.top + 4), left: r.left, width: r.width, maxHeight });
    } else {
      const maxHeight = Math.max(140, Math.min(desired, spaceBelow));
      setPos({ top: r.bottom + 4, bottom: undefined, left: r.left, width: r.width, maxHeight });
    }
  }, []);

  useLayoutEffect(() => { if (open) computePos(); }, [open, computePos]);

  // Dışarı tık → kapat; scroll → KAPATMA, anchor'a göre yeniden konumlandır (#244).
  useEffect(() => {
    if (!open) return undefined;
    const portal = () => document.getElementById('ui-ss-portal-active');
    const onDown = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (portal()?.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = (e) => {
      if (e.target instanceof Node && portal()?.contains(e.target)) return; // liste içi scroll
      // #244 Sayfa kaydırılınca listeyi KAPATMA (arama uçmasın); sadece paneli taşı.
      computePos();
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, computePos]);

  const openPicker = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    setResults([]);
    setHighlight(-1);
    setTimeout(() => inputRef.current?.focus(), 0);
  };
  // #243 Kontrole tekrar tıklayınca aç/kapa (toggle). Eskiden re-openPicker query'yi ''
  // yapıp aynı değerle arama effect'ini tetiklemediği için liste boş kalıyordu.
  const toggleOpen = () => { if (disabled) return; if (open) setOpen(false); else openPicker(); };

  const pick = (opt) => {
    setLabelCache((c) => ({ ...c, [opt.value]: opt.label }));
    onChange(opt.value, opt);
    setOpen(false);
    setQuery('');
  };

  const clear = () => {
    onChange('', null);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (results[highlight]) pick(results[highlight]); }
  };

  const q = query.trim();

  return (
    <div className="ui-ss" ref={wrapRef}>
      <button
        type="button"
        className={`ui-ss-control${disabled ? ' is-disabled' : ''}${open ? ' is-open' : ''}`}
        onClick={toggleOpen}
        disabled={disabled}
      >
        <span className={`ui-ss-value${displayLabel ? '' : ' is-placeholder'}`}>
          {displayLabel || emptyOption || placeholder}
        </span>
        {!disabled && value && emptyOption && (
          <span
            className="ui-ss-clear"
            role="button"
            title="Temizle"
            onClick={(e) => { e.stopPropagation(); clear(); }}
          >
            ×
          </span>
        )}
        <span className="ui-ss-caret" aria-hidden="true">▾</span>
      </button>

      {open && createPortal(
        <div
          id="ui-ss-portal-active"
          className="ui-ss-pop"
          style={{ top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
        >
          <input
            ref={inputRef}
            className="ui-ss-search"
            value={query}
            placeholder={placeholder}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <div className="ui-ss-list" role="listbox" onScroll={onListScroll}>
            {emptyOption && (
              <div className="ui-ss-opt ui-ss-opt--empty" onClick={clear}>{emptyOption}</div>
            )}
            {loading && <div className="ui-ss-msg">Aranıyor…</div>}
            {!loading && results.length === 0 && (paged || q.length >= minChars) && (
              <div className="ui-ss-msg">Sonuç yok</div>
            )}
            {!loading && !paged && q.length < minChars && !emptyOption && (
              <div className="ui-ss-msg">Aramak için yazın…</div>
            )}
            {results.map((opt, i) => (
              <div
                key={opt.value}
                role="option"
                aria-selected={opt.value === value}
                className={`ui-ss-opt${i === highlight ? ' is-active' : ''}${opt.value === value ? ' is-selected' : ''}`}
                onMouseEnter={() => setHighlight(i)}
                onClick={() => pick(opt)}
              >
                {opt.label}
              </div>
            ))}
            {paged && loadingMore && <div className="ui-ss-msg">Yükleniyor…</div>}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
